import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import { Anchor } from "../../../components/anchor";
import {
  SellerCategory,
  SellerProfile,
} from "../../../stores/seller-store/seller-store.model";
import { getCategoryIcon } from "../../../utils/category";
import { normalizeUrl } from "../../../utils/helper";
import { useStores } from "../../../stores";
import {
  ProductResult,
  GetResellerProductsParam,
  GetResellerCategoriesParam,
  GetSellerCategoriesParam,
} from "../../../utils/interfaces";
import { trackEvents } from "../../../utils/analytics";
import { SellerPageRouter } from "../page-router.props";
import { isAuthenticated } from "../../../utils/cookie";

interface SidebarCategoryProps {
  isReseller: boolean;
  sellerData: SellerProfile;
}

interface CategoryCardProps {
  category: SellerCategory;
  sellerData: SellerProfile;
  className?: string;
}

const CategoryCard = ({ category, sellerData }: CategoryCardProps) => {
  const router: SellerPageRouter = useRouter();
  const urlPath = normalizeUrl(router.asPath);
  const params = new URLSearchParams(router.asPath.split("?")[1]);

  const isActive = category.id === params.get("category");

  params.delete("filter");
  params.set("category", category.id);
  const urlParams = params.toString();

  const handleClickCategory = (url: string) => {
    router.push(url, undefined, { shallow: true });
    if (sellerData?.sellerJopan) {
      trackEvents({
        sellerUsername: sellerData?.User?.username,
        eventName: "produk_klik_category",
        eventCategory: "product",
        eventLabel: "product click",
        eventValue: {
          categoryName: category?.name,
        },
        trackers: ["GTM", "AF", "ME"],
      });
    }
  };

  return (
    <div
      className="my-2 flex flex-col items-center text-center snap-normal snap-center cursor-pointer"
      id="product-cat-item-btn"
      date-testid="product-cat-item-btn"
      onClick={() => handleClickCategory(`${urlPath}/?${urlParams}`)}
    >
      <div
        className={classNames(
          "flex items-center justify-center rounded-lg w-10 h-10 border-[1.5px] shadow-light border-primary",
          isActive
            ? "bg-primary-50 font-bold"
            : "bg-transparent"
        )}
      >
        <Image
          src={
            category.icon
              ? getCategoryIcon(category.icon)
              : getCategoryIcon(`/img/icons/default.svg`)
          }
          alt={category.name}
          width="20"
          height="20"
          className="size-5 object-contain"
        />
      </div>
      <div
        className={classNames(
          "text-label-sm mt-[7px] line-clamp-2 px-0.5",
          isActive ? "text-primary" : "text-secondary-gray"
        )}
      >
        {category.name}
      </div>
    </div>
  );
};

export const SidebarCategory: React.FC<SidebarCategoryProps> = observer(
  ({ isReseller, sellerData }) => {
    const { sellerStore, coverageAreaStore, productStore, accountStore } =
      useStores();
    const router: SellerPageRouter = useRouter();
    const urlPath = normalizeUrl(router.asPath);
    const [hasPromoProduct, setHasPromoProduct] = useState<boolean>(false);
    const [hasNewProduct, setHasNewProduct] = useState<boolean>(false);
    const isMounted = useRef(false);

    const checkAnyPromoAndNewProduct = async () => {
      let promoProductCount = 0;
      let newProductCount = 0;
      const productPromoParams: GetResellerProductsParam = {
        username: router.query.seller,
        promo_active: true,
        limit: 1,
      };
      const productNewParams: GetResellerProductsParam = {
        username: router.query.seller,
        is_new: true,
        limit: 1,
      };
      if (isReseller && coverageAreaStore?.selectedCoverageArea?.id) {
        const coverageAreaId = coverageAreaStore?.selectedCoverageArea?.id;
        const resellerProductPromoParams = {
          ...productPromoParams,
          coverage_area_id: coverageAreaId,
        };
        const resellerProductNewParams = {
          ...productNewParams,
          coverage_area_id: coverageAreaId,
        };
        let productPromoResponse = {} as ProductResult;
        let newProductResponse = {} as ProductResult;
        if (isAuthenticated()) {
          const buyerAccount = await accountStore?.getBuyerAccount();
          if (buyerAccount?.trading === true) {
            productPromoResponse = await productStore.getProducts(
              resellerProductPromoParams,
              false
            );
            newProductResponse = await productStore.getProducts(
              resellerProductNewParams,
              false
            );
          } else {
            productPromoResponse = await productStore.getResellerProducts(
              resellerProductPromoParams,
              false
            );
            newProductResponse = await productStore.getResellerProducts(
              resellerProductNewParams,
              false
            );
          }
        } else {
          // productPromoResponse = await productStore.getResellerProducts(resellerProductPromoParams, false)
          newProductResponse = await productStore.getResellerProducts(
            resellerProductNewParams,
            false
          );
        }
        promoProductCount = productPromoResponse?.product?.count;
        newProductCount = newProductResponse?.product?.count;
      }
      if (!isReseller) {
        // const productPromoResponse = await productStore.getProducts(productPromoParams, false)
        const newProductResponse = await productStore.getProducts(
          productNewParams,
          false
        );
        // promoProductCount = productPromoResponse?.product?.count
        newProductCount = newProductResponse?.product?.count;
      }
      setHasPromoProduct(promoProductCount > 0);
      setHasNewProduct(newProductCount > 0);
    };

    const fetchResellerCategories = async () => {
      if (isReseller && coverageAreaStore?.selectedCoverageArea?.id) {
        checkAnyPromoAndNewProduct();
        const params: GetResellerCategoriesParam = {
          username: router.query.seller,
          coverage_area_id: coverageAreaStore?.selectedCoverageArea?.id,
        };
        const { fromapp } = router.query;
        if (fromapp) params.filtered_product_category = true;
        if (isAuthenticated()) {
          const buyerAccount = await accountStore?.getBuyerAccount();
          if (fromapp && buyerAccount.hasReferralCategory) {
            params.filtered_product_category = false;
          }
          if (buyerAccount?.trading === true) {
            sellerStore?.getSellerCategories(params);
          } else {
            sellerStore?.getResellerCategories(params);
          }
        } else {
          sellerStore?.getResellerCategories(params);
        }
      }
    };

    const fetchSellerCategories = async () => {
      if (!isReseller) {
        checkAnyPromoAndNewProduct();
        const params: GetSellerCategoriesParam = {
          username: router.query.seller,
        };
        sellerStore?.getSellerCategories(params);
      }
    };

    useEffect(() => {
      if (isMounted.current) {
        if (isReseller) fetchResellerCategories();
      } else {
        isMounted.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverageAreaStore?.selectedCoverageArea?.id]);

    useEffect(() => {
      if (router.query.seller) {
        if (isReseller) fetchResellerCategories();
        else fetchSellerCategories();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query.seller]);

    const renderAllCategoryCard = () => {
      const params = new URLSearchParams(router.asPath.split("?")[1]);
      const isActive = !params.get("filter") && !params.get("category");
      params.delete("filter");
      params.delete("category");
      const urlParams = params.toString();

      return (
        <Anchor
          className={classNames(
            "mb-2 flex flex-col items-center snap-normal snap-center",
            {
              "pointer-events-none": !sellerData,
            }
          )}
          href={urlParams ? `${urlPath}?${urlParams}` : urlPath}
          shallow
          id="product-cat-item-btn"
        >
          <div
            className={classNames(
              "flex items-center justify-center rounded-lg w-10 h-10 border-[1.5px] shadow-light  border-primary",
              isActive
                ? "bg-primary-50 font-bold"
                : "bg-transparent"
            )}
          >
            <Image
              src={getCategoryIcon(`/img/icons/semua.svg`)}
              alt="Semua"
              width="20"
              height="20"
              className="size-5 object-contain"
            />
          </div>
          <div
            className={classNames(
              "text-label-sm mt-[7px] line-clamp-2 px-0.5",
              isActive ? "text-primary" : "text-secondary-gray"
            )}
          >
            Semua
          </div>
        </Anchor>
      );
    };

    const renderPromoCategoryCard = () => {
      if (!hasPromoProduct) {
        return null;
      }
      const params = new URLSearchParams(router.asPath.split("?")[1]);
      const isActive = params.get("filter") === "promo";
      params.set("filter", "promo");
      params.delete("category");
      const urlParams = params.toString();

      return (
        <Anchor
          className="my-2 flex flex-col items-center snap-normal snap-center"
          href={urlParams ? `${urlPath}?${urlParams}` : urlPath}
          shallow
          id="product-cat-item-btn"
        >
          <div
            className={classNames(
              "flex items-center justify-center rounded-lg w-10 h-10 border-[1.5px] shadow-light border-primary",
              isActive
                ? "bg-primary-50 font-bold"
                : "bg-transparent"
            )}
          >
            <Image
              src={getCategoryIcon(`/img/icons/promo.svg`)}
              alt="Promo"
              width="20"
              height="20"
              className="size-5 object-contain"
            />
          </div>
          <div
            className={classNames(
              "text-label-sm mt-[7px] line-clamp-2 px-0.5",
              isActive ? "text-primary" : "text-secondary-gray"
            )}
          >
            Promo
          </div>
        </Anchor>
      );
    };

    const renderNewCategoryCard = () => {
      if (!hasNewProduct) {
        return null;
      }

      const params = new URLSearchParams(router.asPath.split("?")[1]);
      const isActive = params.get("filter") === "new";
      params.set("filter", "new");
      params.delete("category");
      const urlParams = params.toString();

      return (
        <Anchor
          className="my-2 flex flex-col items-center snap-normal snap-center"
          href={urlParams ? `${urlPath}?${urlParams}` : urlPath}
          shallow
          id="product-cat-item-btn"
        >
          <div
            className={classNames(
              "flex items-center justify-center rounded-lg w-10 h-10 border-[1.5px] shadow-light border-primary",
              isActive
                ? "bg-primary-50 font-bold"
                : "bg-transparent"
            )}
          >
            <Image
              src={getCategoryIcon(`/img/icons/baru.svg`)}
              alt="Baru"
              width="20"
              height="20"
              className="size-5 object-contain"
            />
          </div>
          <div
            className={classNames(
              "text-label-sm mt-[7px] line-clamp-2 px-0.5",
              isActive ? "text-primary" : "text-secondary-gray"
            )}
          >
            Baru
          </div>
        </Anchor>
      );
    };

    const renderCategories = () => {
      return sellerStore?.categories?.map((category: SellerCategory) => (
        <CategoryCard
          key={category.id}
          category={category}
          sellerData={sellerData}
        />
      ));
    };

    return (
      <div className="flex flex-col w-full max-w-[74px] mr-2 py-4 overflow-auto no-scrollbar snap-y select-none pb-[180px]">
        {renderAllCategoryCard()}
        {renderPromoCategoryCard()}
        {renderNewCategoryCard()}
        {renderCategories()}
      </div>
    );
  }
);
